import { LIMIT_USER_FORM_PROJECT } from './../../../data/services/project.service';
import { SelectionModel } from '@angular/cdk/collections';
import {
    Component,
    Input,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
    OnDestroy
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Authorization, CommTypes } from '@data/models/authorization.model';
import { AuthorizationService } from '@data/services/authorization.service';
import { RoleService } from '@data/services/role.service';
import { User } from '@data/models/user.model';
import * as _ from 'lodash';
import { Project } from '@data/models/project.model';
import { Role } from '@data/models/role.model';
import { commSortingDataAccessor } from '@shared/utils/angular-material-tools';
import { MenuCheckboxComponent, checkboxStatusEnum } from '@shared/components/menu-checkbox/menu-checkbox.component';
import { UserService } from '@data/services/user.service';
import { ProjectService } from '@data/services/project.service';
import { Subject } from 'rxjs/internal/Subject';
import { SnackbarService } from '@app/services/snackbar.service';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { App } from '@data/models/app.model';
import { AppService } from '@data/services/app.service';

@Component({

    selector: 'syn-user-authorizations-form',
    templateUrl: 'user-authorizations-form.component.html',
    styleUrls: ['user-authorizations-form.component.scss']
})
export class UserAuthorizationsFormComponent implements OnInit, OnDestroy {

    @Input() isInternal: boolean;
    @Input() authorizationsForm: FormGroup;
    @Input() userId: string;
    @Input() companyId: BehaviorSubject<string>;

    @ViewChildren('rolesSubmenuItem') menuCheckboxesRoles: QueryList<MenuCheckboxComponent>;
    @ViewChildren('communicationSubmenuItem') menuCheckboxesComm: QueryList<MenuCheckboxComponent>;

    public dataTableForm: FormGroup = new FormGroup({});
    public dataSource: MatTableDataSource<Authorization> = new MatTableDataSource<Authorization>();
    public selection = new SelectionModel<Authorization>(true, []);
    public roles: Role[] = [];
    public copyFromUserGroup: FormGroup;

    public relatedUsers$: Observable<User[]>;
    public relatedUsersContentLoader = new Subject<{ term: string; strict: boolean }>();

    public preferenceComm: CommTypes[] = [
        'MSO',
        'INCIDENT',
        'REQUEST',
        'PATCH',
        'CHANGE',
        'BILLING'
    ];

    public displayedColumns: String[] = [
        'select',
        'projectId',
        'name',
        'roles',
        'mso',
        'incident',
        'request',
        'patch',
        'change',
        'billing'
    ];
    public sortActive = 'roles';
    public showPaginator = true;
    public pageSizeOptions: Number[] = [10, 25, 100];
    public pageSize = 10;
    public pageIndex = 0;
    public loading = false;
    public pending = false;
    public hasCompany = false;
    public isLimitRequest = false;
    public rolesDisplayerSelectSortedByApp: {
        [key: string]: Role[];
    }[];
    public roleApps: string[];
    public allApps: App[];

    private subscriptionToCopyFromUserChanges: Subscription;

    constructor(
        private roleService: RoleService,
        private authorizationService: AuthorizationService,
        private userService: UserService,
        private projectService: ProjectService,
        private snackbarService: SnackbarService,
        private formBuilder: FormBuilder,
        private appService: AppService
    ) { }

    get total(): number {
        return this.selection.selected.length;
    }

    get selectionClass(): string {
        const dataLength = this.dataSource.data.length;
        const selectionLength = this.selection.selected.length;
        return selectionLength === 0 ? 'selection--none' : (dataLength === selectionLength ? 'selection--all' : 'selection--partial');
    }

    get actionButtonsDisabled(): boolean {
        return this.selection.selected.length === 0;
    }

    get actionButtonsTitle(): string {
        return this.selection.selected.length === 0 ? 'Veuillez sélectionner au moins un élément.' : '';
    }

    @ViewChild(MatSort) set matSort(sort: MatSort) {
        if (this.dataSource) {
            this.dataSource.sort = sort;
        }
    }
    @ViewChild(MatPaginator) set matPaginator(paginator: MatPaginator) {
        if (this.dataSource) {
            this.dataSource.paginator = paginator;
        }
    }

    async ngOnInit(): Promise<void> {
        this.copyFromUserGroup = this.formBuilder.group({
            copyFromUser: [''],
        });

        this.relatedUsers$ = combineLatest([this.relatedUsersContentLoader, this.companyId]).pipe(
            switchMap(([search, companyId]) => {
                const safeTerm = search.term.replace(/"/g, '');
                return this.userService.findUsersByCompany(companyId, { sort: 'email', filter: safeTerm.trim() });
            }),
        );

        this.appService.findApps().subscribe((apps) => this.allApps = apps);

        this.companyId.subscribe(async (newCompanyId) => {
            if (newCompanyId) {
                this.hasCompany = true;
                this.loading = true;
                this.roles = await this.roleService.getRoles(this.isInternal).toPromise();
                this.roleApps = [... new Set(this.roles.map((role) => role.app))];
                this.rolesDisplayerSelectSortedByApp = this.sortByApp(this.roles);
                await Promise.all([
                    this.loadProjects(newCompanyId, this.userId)
                ]);
                this.isLimitRequest = this.dataSource.data.length === LIMIT_USER_FORM_PROJECT;
            }
            this.loading = false;
        });

        this.subscriptionToCopyFromUserChanges = this.copyFromUserGroup.controls['copyFromUser'].valueChanges.subscribe(
            async (user: User) => {
                if (user) {
                    await this.copyUser(user.id);
                }
            }
        );
    }

    ngOnDestroy(): void {
        this.companyId.unsubscribe();
        this.subscriptionToCopyFromUserChanges.unsubscribe();
    }

    public hasProjects(): boolean {
        return this.dataSource.data.length > 0;
    }

    public resetData(): void {
        this.clearData();

        // set loading to true to display again page loader
        this.loading = true;
    }

    public getTouchedAuthorizations(): Authorization[] {
        const authorizations = _.reduce(this.dataTableForm.controls, (result, value, key) => {
            if (value.touched) {
                result.push(value.value);
            }
            return result;
        }, []);
        return authorizations;
    }

    public updateAuthorization(projectId: string, roleId?: string, appId?: string): void {
        const currentAppRoles = this.rolesDisplayerSelectSortedByApp[appId].map((role) => role.id);
        const updatedAuthorization: Authorization = this.dataTableForm.controls[projectId].value;
        const sourceData = this.dataSource.data[_.findIndex(this.dataSource.data, user => user.projectId === projectId)];

        if (updatedAuthorization.roles.includes(roleId)){
            updatedAuthorization.roles.splice(updatedAuthorization.roles.indexOf(roleId), 1);
            updatedAuthorization.roles = [...updatedAuthorization.roles];

        } else{
            const iteratorAuthRoles = _.cloneDeep(updatedAuthorization.roles);
            for (const currentRole of iteratorAuthRoles){
                if (currentAppRoles.includes(currentRole) && currentAppRoles.includes(roleId)){
                    updatedAuthorization.roles.splice(updatedAuthorization.roles.indexOf(currentRole), 1);
                    updatedAuthorization.roles = [...updatedAuthorization.roles, roleId];
                }
            }
            updatedAuthorization.roles = [...new Set([...updatedAuthorization.roles, roleId])];
        }
        sourceData.roles = updatedAuthorization.roles;
        if (roleId === undefined) {
            setTimeout(() => {
                sourceData.roles = [];
                sourceData.communicationTypes = [];
                this.dataTableForm.controls[projectId].setValue(sourceData);
                this.dataTableForm.controls[projectId].markAsTouched();
                this.authorizationsForm.controls['userAuthorizations'].setValue(Object.values(this.dataTableForm.getRawValue()));
            }, 0);
            return;
        }
        if (sourceData.roles.length === 0) {
            sourceData.communicationTypes = [];
        }

        if (!this.isInternal) {
            if (updatedAuthorization.roles.includes('lockbox-viewer') && updatedAuthorization.roles.length === 1){
                sourceData.communicationTypes = [];
            } else if (updatedAuthorization.roles.includes('billing')){
                sourceData.communicationTypes = ['BILLING'];
            } else{
                sourceData.communicationTypes = ['MSO', 'INCIDENT', 'REQUEST', 'PATCH', 'CHANGE'];
            }
            this.dataTableForm.controls[projectId].setValue(sourceData);
            this.dataTableForm.controls[projectId].markAsTouched();
        }
        this.authorizationsForm.controls['userAuthorizations'].setValue(Object.values(this.dataTableForm.getRawValue()));
    }

    isAllSelected(): boolean {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.filteredData.length;
        return numSelected >= numRows;
    }

    masterToggle(): void | boolean {
        if (this.isAllSelected()) {
            this.selection.clear();
            return false;
        }

        this.dataSource.data.forEach(
            row => this.selection.deselect(row)
        );
        this.dataSource.filteredData.forEach(
            row => this.selection.select(row)
        );
    }

    public communicationIcon(row: Authorization, type: CommTypes): string {
        switch (this.communicationSwitch(row, type)) {
            case 'no-acl':
                return 'minus';
            case 'no-communication':
                return 'close';
            case 'communication':
                return 'check';
        }
    }

    public communicationClass(row: Authorization, type: CommTypes): string {
        switch (this.communicationSwitch(row, type)) {
            case 'no-acl':
                return 'syn-text--muted';
            case 'no-communication':
                return 'syn-text--danger syn--clickable';
            case 'communication':
                return 'syn-text--success syn--clickable';
        }
    }

    public communicationChange(row: Authorization, type: CommTypes): void {
        const authorization = this.dataSource.data[_.findIndex(this.dataSource.data, user => user.projectId === row.projectId)];
        authorization.communicationTypes = authorization.communicationTypes ? authorization.communicationTypes : [];

        switch (this.communicationSwitch(row, type)) {
            case 'no-acl':
                break;
            case 'no-communication':
                authorization.communicationTypes.push(type);
                break;
            case 'communication':
                _.remove(authorization.communicationTypes, communicationType => communicationType === type);
                break;
        }
        this.dataTableForm.controls[row.projectId].setValue(authorization);
        this.dataTableForm.controls[row.projectId].markAsTouched();
        this.authorizationsForm.controls['userAuthorizations'].setValue(Object.values(this.dataTableForm.getRawValue()));
    }

    public updateMenuRolesStatus(): void {
        const roles: { [key: string]: number } = _.transform(this.roles, (accu, role) => accu[role.id] = 0, {});

        this.selection.selected.forEach(user => {
            user.roles.forEach(role => {
                roles[role]++;
            });
        });

        this.menuCheckboxesRoles.toArray().forEach(menuItem => {
            const newStatus = this.getMenuItemStatus(roles[menuItem.label]);
            menuItem.switchStatus(newStatus);
        });
    }

    public updateMenuCommStatus(): void {
        const communicationTypes: { [key: string]: number } = _.transform(this.preferenceComm, (accu, comm) => accu[comm] = 0, {});

        this.selection.selected.forEach(project => {
            project.communicationTypes.forEach(comm => {
                communicationTypes[comm]++;
            });
        });

        this.menuCheckboxesComm.toArray().forEach(menuItem => {
            const newStatus = this.getMenuItemStatus(communicationTypes[menuItem.label]);
            menuItem.switchStatus(newStatus);
        });
    }

    public updateSelectedUsersRoles($event: MouseEvent, menuItem: MenuCheckboxComponent, role: Role): void {
        const selectedIds: string[] = _.map(this.selection.selected, row => row.projectId);

        for (const id of selectedIds) {
            const sourceData = this.dataSource.data[_.findIndex(this.dataSource.data, user => user.projectId === id)];

            if (menuItem.status === checkboxStatusEnum.CHECKED && !sourceData.roles.includes(role.id)) {
                if (this.isInternal) {
                    sourceData.roles.push(role.id);
                } else {
                    sourceData.roles.splice(0, sourceData.roles.length, role.id);
                }
                if (!this.isInternal) {
                    switch (role.id) {
                        case 'billing':
                            sourceData.communicationTypes = ['BILLING'];
                            break;
                        default:
                            sourceData.communicationTypes = ['MSO', 'INCIDENT', 'REQUEST', 'PATCH', 'CHANGE'];
                    }
                }

            } else if (menuItem.status === checkboxStatusEnum.UNCHECKED) {
                _.pull(sourceData.roles, role.id);

                if (!sourceData.roles.length) {
                    sourceData.communicationTypes = [];
                }
            }

            this.dataTableForm.controls[id].setValue(sourceData);
            this.dataTableForm.controls[id].markAsTouched();
            this.authorizationsForm.controls['userAuthorizations'].setValue(Object.values(this.dataTableForm.getRawValue()));
        }

        // Do not close menu
        $event.stopPropagation();
        this.updateMenuRolesStatus();
    }

    public updateSelectedProjectPreferenceCom($event: MouseEvent, menuItem: MenuCheckboxComponent, comm: CommTypes): void {
        const selectedProjectIds: string[] = _.map(this.selection.selected, row => row.projectId);

        for (const projectId of selectedProjectIds) {
            const sourceData = this.dataSource.data[_.findIndex(this.dataSource.data, user => user.projectId === projectId)];

            if (menuItem.status === checkboxStatusEnum.CHECKED) {
                sourceData.communicationTypes.push(comm);
            } else if (menuItem.status === checkboxStatusEnum.UNCHECKED) {
                sourceData.communicationTypes = sourceData.communicationTypes.filter((communicationType) => communicationType !== comm);
            }

            this.dataTableForm.controls[projectId].setValue(sourceData);
            this.dataTableForm.controls[projectId].markAsTouched();
            this.authorizationsForm.controls['userAuthorizations'].setValue(Object.values(this.dataTableForm.getRawValue()));
        }

        // Do not close menu
        $event.stopPropagation();
        this.updateMenuCommStatus();
    }


    public async copyUser(userId: string): Promise<void> {
        this.pending = true;

        const userRoles = await this.authorizationService.getRolesByUser(userId).toPromise();
        const userCommunications = await this.authorizationService.getCommunicationsByUser(userId).toPromise();
        const userAuthorizations = this.authorizationService.getUserAuthorizations(userRoles, userCommunications);

        // quick and dirty fix: recreate everything :-(
        const projects = this.getLoadedProjects();
        // clear data only after retrieving projects list!
        this.clearData();

        let updatedItems = 0;
        projects.forEach((project) => {
            const authorization: Authorization = userAuthorizations.find(
                (userAuth) => userAuth.projectId === project.id || userAuth.projectId === '*'
            ) || new Authorization();

            if (authorization && authorization.roles) {
                updatedItems++;
            }

            this.createProjectFormGroup(project, authorization);
        });
        const formattedData = Object.values(this.dataTableForm.getRawValue());
        this.dataSource.data = formattedData;
        this.authorizationsForm.controls['userAuthorizations'].setValue(formattedData);

        // mark as touched all authorizations to force update
        _.forEach(this.dataTableForm.controls, (control) => {
            control.markAsTouched();
        });

        this.snackbarService.show({
            message: `Les rôles et communications de ${updatedItems} projets ont été mis à jour`,
            type: 'info',
            timeout: 5000
        });

        this.pending = false;
    }

    applyFilter(term: string, input?: MatInput): void {
        this.dataSource.filter = term.trim().toLowerCase();

        if (input) {
            input.value = term;
        }
    }

    public getAppNameById(appId: string): string {
        let appName = '';

        if (this.allApps){
            appName = this.allApps?.find((app) => app.id === appId)?.name ?? '';
        }
        return appName;
    }

    private clearData(): void {
        if (this.hasProjects()) {
            this.dataSource.paginator.firstPage();
            this.dataSource.data = [];
            Object.keys(this.dataTableForm.controls).forEach((c) => {
                this.dataTableForm.removeControl(c);
            });
        }
    }

    private sortByApp(roles: Role[]): { [key: string]: Role[] }[]{
        const sortedRolesByApp: { [key: string]: Role[] }[] = [];

        for (const role of roles){
            sortedRolesByApp[role.app] = sortedRolesByApp[role.app] ?? [];
            sortedRolesByApp[role.app].push(role);
        }

        return sortedRolesByApp;
    }

    private async loadProjects(companyId: string, userId?: string): Promise<void> {
        // get all projects the user can be assigned a role on
        const projects = await this.projectService.findProjectsByUserAndOrCompanyId(userId, companyId).toPromise();
        this.projectsToAuthorizations(projects, this.authorizationsForm.controls['userAuthorizations'].value);
        this.dataSource.sortingDataAccessor = commSortingDataAccessor;
    }

    private projectsToAuthorizations(projects: Project[], userAuthorization: any): void {
        projects.forEach((project) => {
            const authorization: Authorization = userAuthorization.find(
                (userAuth) => userAuth.projectId === project.id || userAuth.projectId === '*'
            ) || new Authorization();

            this.createProjectFormGroup(project, authorization);
        });
        const formattedData = Object.values(this.dataTableForm.getRawValue());
        this.dataSource.data = formattedData;
        this.authorizationsForm.controls['userAuthorizations'].setValue(formattedData);
    }

    /**
     * Return projects list using formgroup content
     */
    private getLoadedProjects(): Project[] {
        return Object.keys(this.dataTableForm.controls).map((projectId) => {
            return (this.dataTableForm.controls[projectId] as FormGroup).controls['project'].value;
        });
    }

    private createProjectFormGroup(project: Project, authorization: Authorization): void {
        this.dataTableForm.controls[project.id] = new FormGroup({
            'roles': new FormControl(authorization && authorization.roles ? authorization.roles.map(e => (e)) : []),
            'communicationTypes': new FormControl(authorization.communicationTypes || []),
            'projectId': new FormControl(project.id),
            'name': new FormControl(project.name),
            'project': new FormControl(project)
        });
    }

    private communicationSwitch(row: Authorization, type: CommTypes): string {
        if (row.communicationTypes && row.communicationTypes.includes(type)) {
            return 'communication';
        }
        if (!_.isEmpty(row.roles)) {
            return 'no-communication';
        }
        return 'no-acl';
    }

    private getMenuItemStatus(nbItemSelect: number): checkboxStatusEnum {
        if (nbItemSelect > 0 && nbItemSelect === this.selection.selected.length) {
            return checkboxStatusEnum.CHECKED;
        } else if (nbItemSelect > 0) {
            return checkboxStatusEnum.INDETERMINATE;
        }
        return checkboxStatusEnum.UNCHECKED;
    }

}
